import { type Control, Controller, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Field, Input } from '~/components/Form';
import { PasswordRequirements } from '~/features/account';
import {
  meetsPasswordMinLengthRequirement,
  meetsPasswordMixedCasingRequirement,
  meetsPasswordNonAlphaRequirement,
} from '~/utils/passwords';

type Props<TFieldValues extends { password: string }> = {
  control: TFieldValues extends { password: string } ? Control<TFieldValues> : never;
};

export const PasswordField = <TFieldValues extends { password: string }>({ control }: Props<TFieldValues>) => {
  const { isSubmitted } = useFormState({ control });

  const { t } = useTranslation(['auth', 'validation']);

  return (
    <Controller
      control={control}
      name="password"
      render={({ field: { name, onBlur, onChange, ref, value }, fieldState: { error, invalid, isDirty, isTouched } }) => (
        <Field.Root dirty={isDirty} invalid={invalid} name={name} touched={isTouched}>
          <Field.Label>{t('auth:registrationPage.stepAccountInformation.form.passwordField.label')}</Field.Label>

          <Input onBlur={onBlur} onValueChange={onChange} ref={ref} type="password" value={value} />

          <Field.Footer>
            <Field.Error match={!!error}>{error?.message}</Field.Error>

            <PasswordRequirements password={value} showInvalid={isTouched || isSubmitted} />
          </Field.Footer>
        </Field.Root>
      )}
      rules={{
        // Rules already covered by `PasswordRequirements` don't have a message here.
        required: { value: true, message: t('validation:required') },
        maxLength: {
          value: 255,
          message: t('validation:maxLength', {
            attribute: t('auth:registrationPage.stepAccountInformation.form.passwordField.label'),
            max: 255,
          }),
        },
        validate: (value) =>
          (meetsPasswordMinLengthRequirement(value) &&
            meetsPasswordMixedCasingRequirement(value) &&
            meetsPasswordNonAlphaRequirement(value)) ||
          t('auth:registrationPage.stepAccountInformation.form.passwordField.requirements.error'),
      }}
    />
  );
};
